import { useLayoutEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import toast from "./toast";

/**
 * Dismisses stale success toasts whenever the route changes, so a success
 * confirmation raised on one page doesn't linger after the user has navigated
 * away from it. Error/warning/info toasts are left alone — they're worth
 * keeping on screen until the user has actually seen them, even across a
 * navigation.
 *
 * Uses useLayoutEffect (not useEffect) so this sweep always runs before the
 * destination route's own passive effects. That matters for a route that
 * raises its own "arrival" toast on mount (e.g. a "record created"
 * confirmation, shown from a useEffect): the sweep reads activeSuccessToastIds
 * before that toast is even created, so it can't be swept by the very
 * navigation that revealed it. A plain useEffect wouldn't give that guarantee
 * — ordering against a different component's passive effect in the same
 * commit would be implicit rather than certain.
 *
 * This says nothing about a toast raised on the page being *left*, as part of
 * the same navigation (e.g. showToast(...); navigate(...)) — that's a separate
 * race, handled independently by pendingRenderSuccessToastIds in toast.jsx,
 * which exempts a just-shown toast from any sweep until it's had a chance to
 * render.
 *
 * Compares against the previously seen pathname (rather than a "first render"
 * flag) so it stays correct under React StrictMode's development-only
 * mount/cleanup/remount cycle, which would otherwise flip a one-shot flag before
 * any real navigation occurred.
 */
export const useDismissToastsOnNavigate = (): void => {
    const { pathname } = useLocation();
    const previousPathnameRef = useRef(pathname);
    const [, , , hideAllSuccessToasts] = toast();

    useLayoutEffect(() => {
        if (previousPathnameRef.current === pathname) {
            return;
        }
        previousPathnameRef.current = pathname;
        hideAllSuccessToasts();
        // hideAllSuccessToasts is a fresh function reference on every call to toast(), so
        // it's intentionally excluded from the dependency array to avoid running on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname]);
};

export default useDismissToastsOnNavigate;
